
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { toast } from "@/hooks/use-toast";

interface Props {
  children: ReactNode;
  fallback: ReactNode;
  componentName?: string; // Optional name to identify where the error occurred
  onError?: (error: Error, errorInfo: ErrorInfo, componentName?: string) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName } = this.props;
    const errorLocation = componentName || 'unknown component';
    
    // Log the error with component information for better debugging
    console.error(`Error in ${errorLocation}:`, error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Set the error info in state for potential display
    this.setState({ errorInfo });
    
    // Call optional error handler function if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, componentName);
    }
    
    // Show toast notification in production
    if (process.env.NODE_ENV === 'production') {
      toast({
        title: "Something went wrong",
        description: `A problem occurred in ${errorLocation}. The team has been notified.`,
        variant: "destructive",
      });
    }
  }

  public render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div>
          {this.props.fallback || (
            <div className="p-4 m-4 bg-red-50 border border-red-200 rounded-md">
              <h2 className="text-lg font-semibold text-red-700">Something went wrong</h2>
              <p className="text-sm text-red-600">
                {this.state.error?.message || "An unexpected error occurred"}
              </p>
              {process.env.NODE_ENV !== 'production' && this.state.errorInfo && (
                <details className="mt-2">
                  <summary className="text-sm text-red-600 cursor-pointer">View technical details</summary>
                  <pre className="mt-2 p-2 text-xs bg-gray-50 border border-gray-200 rounded overflow-auto max-h-[200px]">
                    {this.state.error?.toString()}
                    {'\n'}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Utility function to wrap any component with an error boundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  componentName?: string
) => {
  const WithErrorBoundary = (props: P) => (
    <ErrorBoundary fallback={fallback} componentName={componentName || Component.displayName || Component.name}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WithErrorBoundary.displayName = `WithErrorBoundary(${componentName || Component.displayName || Component.name})`;
  return WithErrorBoundary;
};
